import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Snowflake, Layers, Loader2, Archive, CheckCircle, AlertCircle } from "lucide-react";

type NamedCohort = {
  id: string;
  name: string;
  description: string | null;
  qatt: number | null;
  company: string | null;
  status: string;
  createdBy: string;
  createdAt: string;
  itemCount: number;
};

type FreezeResult = {
  frozen: number;
  skipped: { id: string; reason: string }[];
};

function qattLabel(qatt: number | null) {
  if (qatt === 1) return "QATT 1";
  if (qatt === 3) return "QATT 3";
  return "All";
}

export default function BaselineManager() {
  const { toast } = useToast();

  const [freezeDialogCohort, setFreezeDialogCohort] = useState<NamedCohort | null>(null);
  const [runName, setRunName] = useState("");
  const [freezeResult, setFreezeResult] = useState<FreezeResult | null>(null);

  const [archiveCohort, setArchiveCohort] = useState<NamedCohort | null>(null);

  const { data: cohorts = [], isLoading } = useQuery<NamedCohort[]>({
    queryKey: ["/api/model-measurement/cohorts"],
  });

  const openFreezeDialog = (cohort: NamedCohort) => {
    setFreezeDialogCohort(cohort);
    setRunName(`${cohort.name} – ${new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`);
    setFreezeResult(null);
  };

  const closeFreezeDialog = () => {
    if (freezeMutation.isPending) return;
    setFreezeDialogCohort(null);
    setRunName("");
    setFreezeResult(null);
  };

  const freezeMutation = useMutation({
    mutationFn: async ({ cohort, name }: { cohort: NamedCohort; name: string }) => {
      // 1. Fetch delivery IDs from the cohort (backend requires non-empty array)
      const itemsRes = await apiRequest("GET", `/api/model-measurement/cohorts/${cohort.id}/items`);
      const items: { qaDeliveryId: string }[] = await itemsRes.json();
      const qaDeliveryIds = items.map(i => i.qaDeliveryId);
      if (qaDeliveryIds.length === 0) throw new Error("Cohort has no items");

      // 2. Create a draft baseline run tagged with this cohort
      const createRes = await apiRequest("POST", "/api/model-measurement/baseline-runs", {
        name,
        selectionMode: "manual",
        cohortId: cohort.id,
        cohortName: cohort.name,
        qaDeliveryIds,
      });
      const run = await createRes.json();

      // 3. Freeze items from the cohort into the run
      const freezeRes = await apiRequest("POST", `/api/model-measurement/baseline-runs/${run.id}/freeze-cohort`, {
        cohortId: cohort.id,
      });
      return freezeRes.json() as Promise<FreezeResult>;
    },
    onSuccess: (data) => {
      setFreezeResult(data);
      toast({
        title: "Baseline Run Frozen",
        description: `${data.frozen} deliveries frozen${data.skipped.length > 0 ? `, ${data.skipped.length} skipped` : ""}`,
      });
    },
    onError: (error: Error) => {
      toast({ title: "Freeze Failed", description: error.message, variant: "destructive" });
    },
  });

  const archiveMutation = useMutation({
    mutationFn: async (cohortId: string) => {
      const res = await apiRequest("PATCH", `/api/model-measurement/cohorts/${cohortId}`, { status: "archived" });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/model-measurement/cohorts"] });
      setArchiveCohort(null);
      toast({ title: "Cohort archived" });
    },
    onError: (error: Error) => {
      toast({ title: "Archive failed", description: error.message, variant: "destructive" });
    },
  });

  const activeCohorts = cohorts.filter(c => c.status === "active");
  const archivedCohorts = cohorts.filter(c => c.status === "archived");

  const renderCohortCard = (cohort: NamedCohort) => (
    <Card
      key={cohort.id}
      data-testid={`card-cohort-${cohort.id}`}
      className="flex flex-col gap-0"
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-base truncate" data-testid={`text-cohort-name-${cohort.id}`}>
              {cohort.name}
            </CardTitle>
            {cohort.description && (
              <CardDescription className="mt-0.5 text-xs line-clamp-2" data-testid={`text-cohort-desc-${cohort.id}`}>
                {cohort.description}
              </CardDescription>
            )}
          </div>
          <Badge
            variant="secondary"
            className="shrink-0 tabular-nums"
            data-testid={`badge-cohort-count-${cohort.id}`}
          >
            {cohort.itemCount} delivery{cohort.itemCount !== 1 ? "ies" : ""}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex flex-wrap gap-1.5 mb-3">
          <Badge variant="outline" className="text-xs" data-testid={`badge-cohort-qatt-${cohort.id}`}>
            {qattLabel(cohort.qatt)}
          </Badge>
          {cohort.company && (
            <Badge variant="outline" className="text-xs" data-testid={`badge-cohort-company-${cohort.id}`}>
              {cohort.company}
            </Badge>
          )}
          <span className="text-xs text-muted-foreground ml-auto" data-testid={`text-cohort-created-${cohort.id}`}>
            {new Date(cohort.createdAt).toLocaleDateString()}
          </span>
        </div>
        {cohort.status === "active" && (
          <div className="flex gap-2">
            <Button
              size="sm"
              className="flex-1"
              onClick={() => openFreezeDialog(cohort)}
              disabled={cohort.itemCount === 0}
              data-testid={`button-freeze-cohort-${cohort.id}`}
            >
              <Snowflake className="h-3.5 w-3.5 mr-1.5" />
              Freeze from Cohort
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-muted-foreground"
              onClick={() => setArchiveCohort(cohort)}
              data-testid={`button-archive-cohort-${cohort.id}`}
            >
              <Archive className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <div
          className="flex h-9 w-9 items-center justify-center rounded-md shrink-0"
          style={{ background: "#D97706" }}
        >
          <Layers className="h-4 w-4 text-white" />
        </div>
        <div>
          <h1 className="text-lg font-semibold leading-tight" data-testid="heading-cohorts">
            Named Cohorts
          </h1>
          <p className="text-sm text-muted-foreground">
            Saved delivery sets for repeatable baseline freezes
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground" data-testid="loading-cohorts">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          Loading cohorts…
        </div>
      ) : cohorts.length === 0 ? (
        <Card>
          <CardContent className="py-14 text-center text-muted-foreground" data-testid="empty-cohorts">
            <Layers className="h-8 w-8 mx-auto mb-3 opacity-30" />
            <p className="font-medium">No cohorts yet</p>
            <p className="text-sm mt-1">
              Select deliveries in the QA List and use{" "}
              <span className="font-medium">Save as Cohort</span> to create one.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {activeCohorts.length > 0 && (
            <section>
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3" data-testid="section-active-cohorts">
                Active ({activeCohorts.length})
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {activeCohorts.map(renderCohortCard)}
              </div>
            </section>
          )}

          {archivedCohorts.length > 0 && (
            <section>
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3" data-testid="section-archived-cohorts">
                Archived ({archivedCohorts.length})
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 opacity-60">
                {archivedCohorts.map(renderCohortCard)}
              </div>
            </section>
          )}
        </div>
      )}

      {/* Freeze from Cohort dialog */}
      <Dialog
        open={!!freezeDialogCohort}
        onOpenChange={(open) => { if (!open) closeFreezeDialog(); }}
      >
        <DialogContent data-testid="dialog-freeze-cohort">
          <DialogHeader>
            <DialogTitle>Freeze from Cohort</DialogTitle>
            <DialogDescription>
              Create a new baseline run from{" "}
              <span className="font-medium">{freezeDialogCohort?.name}</span>
              {" "}({freezeDialogCohort?.itemCount} deliveries).
            </DialogDescription>
          </DialogHeader>

          {!freezeResult ? (
            <>
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="freeze-run-name">
                  Run Name
                </label>
                <Input
                  id="freeze-run-name"
                  value={runName}
                  onChange={(e) => setRunName(e.target.value)}
                  placeholder="e.g. My Cohort – May 2026"
                  disabled={freezeMutation.isPending}
                  data-testid="input-freeze-run-name"
                />
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={closeFreezeDialog}
                  disabled={freezeMutation.isPending}
                  data-testid="button-freeze-cohort-cancel"
                >
                  Cancel
                </Button>
                <Button
                  disabled={freezeMutation.isPending || !runName.trim()}
                  onClick={() => {
                    if (freezeDialogCohort && runName.trim()) {
                      freezeMutation.mutate({ cohort: freezeDialogCohort, name: runName.trim() });
                    }
                  }}
                  data-testid="button-freeze-cohort-confirm"
                >
                  {freezeMutation.isPending ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Freezing…</>
                  ) : (
                    <><Snowflake className="h-4 w-4 mr-2" />Freeze {freezeDialogCohort?.itemCount} Deliveries</>
                  )}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className="rounded-md border p-4 space-y-2" data-testid="freeze-cohort-results">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  {freezeResult.frozen} deliveries frozen
                </div>
                {freezeResult.skipped.length > 0 && (
                  <div className="flex items-start gap-2 text-sm text-muted-foreground">
                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0 text-amber-500" />
                    <div>
                      <span className="font-medium text-foreground">{freezeResult.skipped.length} skipped</span>
                      <div className="mt-1 max-h-28 overflow-auto space-y-0.5">
                        {freezeResult.skipped.map((s) => (
                          <div key={s.id} className="text-xs font-mono">
                            {s.id.slice(0, 8)}… — {s.reason.replace(/_/g, " ")}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button onClick={closeFreezeDialog} data-testid="button-freeze-cohort-close">
                  Done
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Archive confirmation dialog */}
      <Dialog
        open={!!archiveCohort}
        onOpenChange={(open) => { if (!open && !archiveMutation.isPending) setArchiveCohort(null); }}
      >
        <DialogContent data-testid="dialog-archive-cohort">
          <DialogHeader>
            <DialogTitle>Archive cohort?</DialogTitle>
            <DialogDescription>
              <span className="font-medium">{archiveCohort?.name}</span> will be archived and can no
              longer be frozen. Existing baseline runs are unaffected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setArchiveCohort(null)}
              disabled={archiveMutation.isPending}
              data-testid="button-archive-cohort-cancel"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={archiveMutation.isPending}
              onClick={() => archiveCohort && archiveMutation.mutate(archiveCohort.id)}
              data-testid="button-archive-cohort-confirm"
            >
              {archiveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Archive"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
